import React from "react";
import { commitMutation, graphql } from "react-relay";
import TextArea from "react-textarea-autosize";

import { faArrowLeft, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, noop } from "lodash";
import { Popup } from "semantic-ui-react";

import { BillingIdentity } from "~/billing";
import EmailListInput from "~/common/EmailListInput";
import { fetchFeature } from "~/lib/flippy";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isCtrlOrCmd, isTeams } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { HelpCenterIcon, IconButton, Input, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import UserMultiSelector, {
	defaultLoadOptions,
	type MultiValueType,
	type UserMultiSelectorOption,
} from "../common/UserSelect/UserMultiSelector";

import type { BillingIdentityFeaturesPaywall_viewer$key as PaywallViewer } from "~/__generated__/BillingIdentityFeaturesPaywall_viewer.graphql";
import type { CreateStreamModalMutation as CreateMutationType } from "~/__generated__/CreateStreamModalMutation.graphql";

import styles from "./css/CreateStreamModal.module.css";

const SHARE_INPUT_PLACEHOLDER = "Add one or more teammates by name or email…";

const CreateMutation = graphql`
	mutation CreateStreamModalMutation($input: CreatePersonalStreamInput!) {
		createPersonalStream(input: $input) {
			stream {
				id
			}
			homePanelGroup {
				...HomePanelGroup_group
			}
		}
	}
`;

function createStream(name: string, description: string, shareWithUserEmails: string[], groupId?: string) {
	dispatchAnalyticsTrack("Personal Series Created", { source: "panel" });

	return new Promise<string>((resolve, reject) => {
		commitMutation<CreateMutationType>(environment, {
			mutation: CreateMutation,
			variables: { input: { name, description, shareWith: shareWithUserEmails, createNote: true, groupId: groupId } },
			onCompleted: ({ createPersonalStream }, errors) => {
				if (errors?.length) {
					reject();
					errors.forEach(error => toast.error(error.message));
				} else if (!createPersonalStream) {
					reject();
					toast.error("Something went wrong; please contact support.");
					throw new Error(`Unexpected output from CreatePersonalStream: ${createPersonalStream}`);
				} else {
					resolve(createPersonalStream.stream.id);
				}
			},
		});
	});
}

interface State {
	name: string;
	defaultUsers?: readonly UserMultiSelectorOption[];
	description: string;
	/** All email values present in sharees or EmailListInput */
	shareWithUserEmails: string[];
	/** The individual User objects to render the Selector with */
	sharees: readonly UserMultiSelectorOption[];
	loading: boolean;
	isCrossWorkspaceEnabled: boolean;
}

export type OnCreatedType = (streamId: string) => void;

interface Props {
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	getCreateFunction?: (onCreate: typeof CreateStreamModalContent.prototype.onCreate) => void;
	onNameChange?: (name: string) => void;
	onDescriptionChange?: (name: string) => void;
	viewer: PaywallViewer & {
		user: { id: string; email: string };
		account: {
			allowInviting: boolean | null;
			guestUsersAllowInvite: boolean | null;
			allowExternalInviting: boolean | null;
		};
	};
	onClose?: () => void;
	onBack?: () => void;
	onCreated: OnCreatedType;
	groupId?: string;
	hideDescription?: boolean;
}

export type CreateStreamModalViewer = Props["viewer"];

/** UserSelect and EmailListInput give values in different, highly variable formats. We only need emails. */
type Sharee = { email: string } | { user: { email: string } | null };

export class CreateStreamModalContent extends React.PureComponent<Props, State> {
	state: State = {
		name: "",
		defaultUsers: undefined,
		description: "",
		shareWithUserEmails: [],
		sharees: [],
		loading: false,
		isCrossWorkspaceEnabled: false,
	};

	componentDidMount() {
		if (this.props.getCreateFunction) {
			this.props.getCreateFunction(this.onCreate.bind(this));
		}

		const { viewer } = this.props;

		defaultLoadOptions({ excludeUserIDs: [viewer.user.id], ranked: true }).then(data => {
			if (!data) return;

			const { viewer } = data;

			const options =
				viewer.selectUsers?.edges.map(user => {
					return {
						value: user.node.id,
						label: user.node.fullName,
						email: user.node.email,
						avatar: {
							id: user.node.id,
							fullName: user.node.fullName,
							isGuest: user.node.isGuest,
							image: user.node.image,
						},
					};
				}) ?? [];

			this.setState({ defaultUsers: options as UserMultiSelectorOption[] });
		});
		this.getCrossWorkspaceFeatureFlag();
	}

	onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ name: event.target.value });
		if (this.props.onNameChange) {
			this.props.onNameChange(event.target.value);
		}
	};

	onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		const description = event.target.value;
		this.setState({ description });
		if (this.props.onDescriptionChange) {
			this.props.onDescriptionChange(description);
		}
	};

	/**
	 * This basically just wrangles the email addresses out of UserSelect and EmailListInput's all-over-the-place data
	 * formats into a flat list of non-null strings.
	 */
	onShareesChange = (val: readonly Sharee[] | Sharee | null | undefined) => {
		let newUsers = val ? ("length" in val ? val : [val]) : [];
		let emails = compact(newUsers.map(sharee => ("user" in sharee ? sharee.user?.email : sharee.email)));
		this.setState({ shareWithUserEmails: emails });
	};

	/** This function is specifically for the UserMultiSelector and has no correlation with onShareesChange  */
	onShareesChangeUserSelector = (sharees: MultiValueType<UserMultiSelectorOption>) => {
		const emails = compact(sharees.map(sharee => sharee.email));
		this.setState({ sharees: sharees, shareWithUserEmails: emails });
	};

	onCreate = () => {
		const { name, description, shareWithUserEmails } = this.state;
		const { groupId, onClose } = this.props;

		this.setState({ loading: true });

		return createStream(name, description, shareWithUserEmails, groupId)
			.then(id => {
				this.props.onCreated(id);
				return id;
			})
			.finally(() => {
				this.setState({ loading: false });
				if (onClose) onClose();
			});
	};

	submitOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key == "Enter" && !this.state.loading) this.onCreate();
	};

	submitOnCmdEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (isCtrlOrCmd(event.nativeEvent) && event.key == "Enter" && !this.state.loading) this.onCreate();
	};

	getCrossWorkspaceFeatureFlag = async () => {
		const isCrossWorkspaceEnabled = await fetchFeature("cross_workspace_collaboration", "account");
		this.setState({ isCrossWorkspaceEnabled });
	};

	render() {
		const { name, description, shareWithUserEmails, sharees, loading, defaultUsers, isCrossWorkspaceEnabled } =
			this.state;
		const { onClose, onBack, viewer, hideDescription } = this.props;

		const internalInvitesAllowed = viewer.account.allowInviting ?? false;
		const guestInvitesAllowed = viewer.account.guestUsersAllowInvite ?? false;
		const externalInvitesAllowed = (viewer.account.allowExternalInviting ?? false) && isCrossWorkspaceEnabled;

		return (
			<>
				<Modal.Header>
					{onClose && (
						<>
							<Modal.Title>Create a new series</Modal.Title>
							<Modal.HeaderActions onClose={onClose}>
								<HelpCenterIcon articleId="4645985" title="Navigating series" className={styles.headerButton} />
							</Modal.HeaderActions>
						</>
					)}
					{onBack && (
						<>
							<IconButton title="Back" icon={faArrowLeft} onClick={onBack} className={styles.backIcon} />
							<Modal.Title className={styles.modalTitle}>Create a new series</Modal.Title>
							<HelpCenterIcon articleId="4645985" title="Navigating series" className={styles.headerButton} />
						</>
					)}
				</Modal.Header>
				<Modal.Body className={isTeams ? styles.teamsBody : undefined}>
					<label>
						<small className={styles.fieldLabel}>Series name</small>
						<div className={styles.inputContainer}>
							<Input
								className={styles.inputField}
								value={name}
								onChange={this.onNameChange}
								placeholder="Name this series… (max 255 chars)"
								maxLength={255}
								onKeyPress={this.submitOnEnter}
								autoFocus
								data-test-id="create-personal-stream-modal-title-input"
								name="stream_name"
							/>
						</div>
					</label>
					{!hideDescription && (
						<label>
							<small className={styles.fieldLabel}>Description</small>
							<div className={styles.inputContainer}>
								<TextArea
									className={cx(styles.inputField, styles.textArea)}
									value={description}
									onChange={this.onDescriptionChange}
									placeholder="Keep everyone aligned by describing the purpose of this series. (max 255 chars)"
									maxLength={255}
									minRows={1}
									maxRows={3}
									onKeyDown={this.submitOnCmdEnter}
								/>
							</div>
						</label>
					)}
					<label>
						<small className={styles.fieldLabel}>
							Share with (optional)
							<Popup
								inverted
								position="top center"
								size="mini"
								trigger={<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />}
								content="These members will have access to all notes in the series"
								className={styles.tooltip}
							/>
						</small>
						{internalInvitesAllowed || guestInvitesAllowed || externalInvitesAllowed ? (
							<EmailListInput
								isMulti
								useNewTeammateBtn
								internalInvitesAllowed={internalInvitesAllowed}
								guestInvitesAllowed={guestInvitesAllowed}
								externalInvitesAllowed={externalInvitesAllowed}
								exclude={[viewer.user.email]}
								placeholder={SHARE_INPUT_PLACEHOLDER}
								onChange={this.onShareesChange}
							/>
						) : (
							<UserMultiSelector
								ranked
								defaultUsers={defaultUsers}
								excludeUserIDs={[viewer.user.id]}
								isLoading={defaultUsers === undefined}
								placeholder={SHARE_INPUT_PLACEHOLDER}
								selectedUsers={sharees}
								onChange={this.onShareesChangeUserSelector}
							/>
						)}
					</label>
				</Modal.Body>
				{!isTeams && (
					<Modal.Footer>
						<BillingIdentity
							feature="sharing"
							viewer={viewer}
							iconInheritColor
							allowAccess={shareWithUserEmails.length < 1}
						>
							{(extraProps, billingIcon, paywalled) => (
								<Button
									size="normal"
									variant={paywalled ? "billing" : "primary"}
									disabled={loading}
									onClick={this.onCreate}
									{...extraProps}
								>
									{billingIcon && <>{billingIcon}&ensp;</>}Create
								</Button>
							)}
						</BillingIdentity>
					</Modal.Footer>
				)}
			</>
		);
	}
}

class CreateStreamModal extends React.PureComponent<Props, State> {
	render() {
		const { onClose, onBack, onCreated, viewer, getCreateFunction, groupId } = this.props;
		return (
			<Modal
				onClose={onClose || onBack || noop}
				width="27.5rem"
				className={onBack ? styles.fullHeight : undefined}
				data-test-id="create-personal-stream-modal"
			>
				<CreateStreamModalContent
					onCreated={onCreated}
					onClose={onClose}
					viewer={viewer}
					getCreateFunction={getCreateFunction}
					onBack={onBack}
					groupId={groupId}
				/>
			</Modal>
		);
	}
}

export default CreateStreamModal;
